
import WhoAreWe from "@/components/admin/Content/WebPages/Home/WhoAreWe"
import { useWebPageForm } from "@/contexts/WebPageFormContext";
import HeroSection from "@/components/admin/Content/WebPages/Home/HeroSection";
import StrategicPillars from "@/components/admin/Content/WebPages/Home/StrategicPillars";
import WhatWeDo from "@/components/admin/Content/WebPages/Home/WhatWeDo";
import PastEvents from "@/components/admin/Content/WebPages/Home/PastEvents";
import VideoHighlight from "@/components/admin/Content/WebPages/Home/VideoHighlight";
import ImageHighlight from "@/components/admin/Content/WebPages/Home/ImageHighlight";
import KeyHighlightsHeader from "@/components/admin/Content/WebPages/Home/KeyHighlightsHeader";
import FullPageSpinner from "@/components/ui/FullPageSpinner";
import Programmes from "@/components/admin/Content/WebPages/Home/Programmes";
import UpcomingEvents from "@/components/admin/Content/WebPages/Home/UpcomingEvents";
import PressRelease from "@/components/admin/Content/WebPages/Home/PressRelease";
import MultimediaPhotos from "@/components/admin/Content/WebPages/Home/MultimediaPhotos";
import MultimediaVideos from "@/components/admin/Content/WebPages/Home/MultimediaVideos";
import FeaturesCard from "@/components/admin/Content/WebPages/Home/FeaturesCard";
import KeyResources from "@/components/admin/Content/WebPages/Home/KeyResources";

const Home = () => {
  const { pageData, isLoading } = useWebPageForm();

  if (isLoading) {
    return <FullPageSpinner />;
  }


  const whoAreWeSubSectionData = [
    pageData?.HOME_HISTORICAL_TIMELINE,
    pageData?.HOME_LEADERSHIP,
    pageData?.HOME_MEMBER_COUNTRIES
  ]



  return (
    <div className="flex flex-col gap-8 lg:gap-12 p-4 md:p-8 2xl:p-12 2xl:w-4/5 mx-auto max-w-[1920px]">
      {/* Hero Section - Always visible, uses press release highlights API */}
      <HeroSection data={pageData?.HERO_SECTION ?? {
        sectionTag: 'HERO_SECTION',
        contentType: 'PRESS_RELEASE_HIGHLIGHTS',
        title: 'Hero Section',
        description: 'Featured press releases for the landing page hero section'
      }} />

      <WhoAreWe data={pageData?.HOME_WHO_ARE_WE ?? {
        sectionTag: 'HOME_WHO_ARE_WE',
        contentType: 'WITHOUT_FILE',
        sectionTitle: 'Who Are We',
        title: 'Who Are We',
        description: 'Introduction section for the home page'
      }} />

      <div className="grid gap-4 lg:gap-8 md:grid-cols-2 lg:grid-cols-3 ">
        {whoAreWeSubSectionData.map((section, index) => {
          const defaults = [
            { sectionTag: 'HOME_HISTORICAL_TIMELINE', title: 'Historical Timeline', contentType: 'WITHOUT_FILE' },
            { sectionTag: 'HOME_LEADERSHIP', title: 'Leadership', contentType: 'WITHOUT_FILE' },
            { sectionTag: 'HOME_MEMBER_COUNTRIES', title: 'Member Countries', contentType: 'WITHOUT_FILE' }
          ];

          return <FeaturesCard key={index} data={section ?? defaults[index]} />
        })}
      </div>

      <div className="grid gap-4 md:gap-8 md:grid-cols-2 ">
        {/* Strategic Pillars - Always visible */}
        <StrategicPillars data={pageData?.HOME_PRIORITIES_OF_AFSA ?? {
          sectionTag: 'HOME_PRIORITIES_OF_AFSA',
          contentType: 'STRATEGIC_PILLAR_HIGHLIGHTS',
          title: 'Priorities of AFSA',
          description: 'Strategic pillars for the landing page'
        }} />

        {/* Programmes - Always visible */}
        <Programmes data={pageData?.HOME_AFSA_PROGRAMME ?? {
          sectionTag: 'HOME_AFSA_PROGRAMME',
          contentType: 'PROGRAMME_HIGHLIGHTS',
          title: 'AFSA Programme',
          description: 'Programmes for the landing page'
        }} />
      </div>

      <WhatWeDo data={pageData?.HOME_WHAT_WE_DO ?? {
        sectionTag: 'HOME_WHAT_WE_DO',
        contentType: 'WITHOUT_FILE',
        sectionTitle: 'What We Do',
        title: 'What We Do',
        description: 'Description of activities'
      }} />

      <div className="grid gap-4 lg:grid-cols-3 lg:gap-8">
        <UpcomingEvents data={pageData?.HOME_EVENTS_TO_LOOK_OUT_FOR ?? {
          sectionTag: 'HOME_EVENTS_TO_LOOK_OUT_FOR',
          contentType: 'EVENT_HIGHLIGHTS',
          sectionTitle: 'Events to Look Out For',
          title: 'Upcoming Events'
        }} />
        <PastEvents data={pageData?.HOME_EVENTS_OF_AFSA ?? {
          sectionTag: 'HOME_EVENTS_OF_AFSA',
          contentType: 'EVENT_HIGHLIGHTS',
          sectionTitle: 'Events of AFSA',
          title: 'Past Events'
        }} />
        <PressRelease data={pageData?.HOME_STAY_IN_THE_LOOP_OF_AFSA ?? {
          sectionTag: 'HOME_STAY_IN_THE_LOOP_OF_AFSA',
          contentType: 'PRESS_RELEASE_HIGHLIGHTS',
          sectionTitle: 'Stay in the Loop of AFSA',
          title: 'Latest Press Releases'
        }} />
      </div>

      {/* Key Highlights Section */}
      <div className="flex flex-col gap-4">
        {/* Editable Key Highlights Header */}
        <KeyHighlightsHeader data={pageData?.HOME_THROUGH_THE_LENS_OF_AFSA ?? {
          sectionTag: 'HOME_THROUGH_THE_LENS_OF_AFSA',
          contentType: 'WITHOUT_FILE',
          sectionTitle: 'Through the Lens of AFSA',
          description: 'Featured video and image highlights'
        }} />
        {/* Separate Video and Image Highlight Editors */}
        <div className="grid gap-4 lg:grid-cols-2 lg:gap-8">
          <VideoHighlight data={pageData?.HOME_VIDEO_HIGHLIGHT ?? {
            sectionTag: 'HOME_VIDEO_HIGHLIGHT',
            contentType: 'VIDEO_HIGHLIGHTS',
            sectionTitle: 'Video Highlight',
            description: 'Featured video highlight for the home page'
          }} />
          <ImageHighlight data={pageData?.HOME_IMAGE_HIGHLIGHTS ?? {
            sectionTag: 'HOME_IMAGE_HIGHLIGHTS',
            contentType: 'IMAGE_HIGHLIGHTS',
            sectionTitle: 'Image Highlights',
            description: 'Featured image highlights for the home page'
          }} />
        </div>
      </div>

      {/* Multimedia */}
      <div className="grid gap-4 lg:grid-cols-2 lg:gap-8">
        <MultimediaPhotos data={pageData?.HOME_MULTIMEDIA_PHOTO ?? {
          sectionTag: 'HOME_MULTIMEDIA_PHOTO',
          contentType: 'IMAGE_HIGHLIGHTS',
          sectionTitle: 'Multimedia Photos',
          title: 'Photo Gallery'
        }} />
        <MultimediaVideos data={pageData?.HOME_MULTIMEDIA_VIDEO ?? {
          sectionTag: 'HOME_MULTIMEDIA_VIDEO',
          contentType: 'VIDEO_HIGHLIGHTS',
          sectionTitle: 'Multimedia Videos',
          title: 'Video Gallery'
        }} />
      </div>

      {/* Key Resources - Always visible */}
      <KeyResources data={pageData?.HOME_KEY_RESOURCES ?? {
        sectionTag: 'HOME_KEY_RESOURCES',
        contentType: 'PUBLICATION_HIGHLIGHTS',
        title: 'Key Resources',
        description: 'Featured publications for the home page'
      }} />

    </div>
  )
}

export default Home